// src/components/AdminDashboard/ManageCollegeAdmin.js
import React, { useState, useEffect } from 'react';
import './ManageAdmin.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaPhone,
  FaSearch,
  FaTimes,
  FaSave,
  FaKey,
  FaUserShield,
  FaBuilding,
  FaMobileAlt,
  FaLock,
  FaUnlock,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

const ManageCollegeAdmin = () => {
  const [collegeAdmins, setCollegeAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view', 'password'
  const [selectedCollegeAdmin, setSelectedCollegeAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    department: '',
    phone: '',
    mobile: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchCollegeAdmins();
  }, []);

  const fetchCollegeAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/college-admins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCollegeAdmins(data.collegeAdmins || []);
      } else {
        setError('Failed to fetch college admins');
      }
    } catch (error) {
      console.error('Error fetching college admins:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create'
        ? `${collegeConfig.api.baseURL}/college-admins`
        : `${collegeConfig.api.baseURL}/college-admins/${selectedCollegeAdmin._id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        fetchCollegeAdmins();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving college admin:', error);
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (collegeAdminId) => {
    if (!window.confirm('Are you sure you want to deactivate this college admin account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/college-admins/${collegeAdminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        fetchCollegeAdmins();
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error deleting college admin:', error);
      setError('Error connecting to server');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/college-admins/${selectedCollegeAdmin._id}/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            newPassword: passwordData.newPassword
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        closeModal();
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Error connecting to server');
    }
  };

  const openModal = (mode, collegeAdmin = null) => {
    setModalMode(mode);
    setSelectedCollegeAdmin(collegeAdmin);
    setError('');

    if (mode === 'create') {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        department: '',
        phone: '',
        mobile: ''
      });
    } else if (collegeAdmin && mode !== 'password') {
      setFormData({
        email: collegeAdmin.email,
        password: '', // Don't populate password for edit
        firstName: collegeAdmin.profile.firstName,
        lastName: collegeAdmin.profile.lastName,
        department: collegeAdmin.profile.department || '',
        phone: collegeAdmin.profile.phone || '',
        mobile: collegeAdmin.profile.mobile || ''
      });
    }

    if (mode === 'password') {
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCollegeAdmin(null);
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
  };

  // Filter college admins based on search
  const filteredCollegeAdmins = collegeAdmins.filter(admin => {
    const fullName = `${admin.profile.firstName} ${admin.profile.lastName}`.toLowerCase();
    const email = admin.email.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-admin-loading">
        <div className="spinner"></div>
        <p>Loading college admins...</p>
      </div>
    );
  }

  return (
    <div className="manage-admin-container">
      {/* Header Section */}
      <div className="manage-admin-header">
        <div className="header-content">
          <h2>Manage College Admins</h2>
          <p>Add college admins who can manage teachers, students, and notices</p>
        </div>
        <button
          className="btn-create-admin"
          onClick={() => openModal('create')}
        >
          <FaUserShield /> Add New College Admin
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success"><FaCheckCircle /> {success}</div>}
      {error && <div className="alert alert-error"><FaExclamationTriangle /> {error}</div>}

      {/* Search Bar */}
      <div className="admin-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search college admins by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-stats">
          <div className="stat-badge">
            <FaUserShield />
            <span>{collegeAdmins.length} Total College Admins</span>
          </div>
          <div className="stat-badge">
            <FaCheckCircle />
            <span>{collegeAdmins.filter(a => a.isActive).length} Active</span>
          </div>
        </div>
      </div>

      {/* College Admins Grid */}
      <div className="admins-grid">
        {filteredCollegeAdmins.length === 0 ? (
          <div className="no-admins">
            <FaUserShield className="no-admins-icon" />
            <h3>No college admins found</h3>
            <p>Add college admins to let them manage teachers, students, and notices</p>
          </div>
        ) : (
          filteredCollegeAdmins.map(admin => (
            <div key={admin._id} className="admin-card">
              <div className="admin-card-header">
                <div className="admin-avatar">
                  <FaUserShield />
                </div>
                <div className="admin-info">
                  <h3>{admin.profile.firstName} {admin.profile.lastName}</h3>
                  <p className="admin-email">{admin.email}</p>
                </div>
                <div className="admin-actions">
                  <button
                    className="btn-icon btn-view"
                    onClick={() => openModal('view', admin)}
                    title="View Details"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => openModal('edit', admin)}
                    title="Edit College Admin"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="btn-icon btn-password"
                    onClick={() => openModal('password', admin)}
                    title="Change Password"
                  >
                    <FaKey />
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(admin._id)}
                    title="Delete College Admin"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="admin-card-body">
                <div className="admin-details">
                  {admin.profile.department && (
                    <div className="detail-item">
                      <FaBuilding className="detail-icon" />
                      <span>{admin.profile.department}</span>
                    </div>
                  )}
                  {admin.profile.phone && (
                    <div className="detail-item">
                      <FaPhone className="detail-icon" />
                      <span>{admin.profile.phone}</span>
                    </div>
                  )}
                  {admin.profile.mobile && (
                    <div className="detail-item">
                      <FaMobileAlt className="detail-icon" />
                      <span>{admin.profile.mobile}</span>
                    </div>
                  )}
                </div>

                <div className="admin-status">
                  <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                    {admin.isActive ? (
                      <><FaUnlock /> Active</>
                    ) : (
                      <><FaLock /> Inactive</>
                    )}
                  </span>
                  <span className="created-date">
                    Added: {new Date(admin.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New College Admin' :
                 modalMode === 'edit' ? 'Edit College Admin' :
                 modalMode === 'password' ? 'Change Password' : 'College Admin Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {modalMode === 'password' ? (
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder="Re-enter new password"
                    required
                  />
                </div>

                <div className="info-message">
                  <FaExclamationTriangle />
                  You are changing the password for {selectedCollegeAdmin.profile.firstName} {selectedCollegeAdmin.profile.lastName}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    <FaKey /> Change Password
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : modalMode !== 'view' ? (
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>

                  {modalMode === 'create' && (
                    <div className="form-group">
                      <label>Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Minimum 6 characters"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Department</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      placeholder="e.g., Administration"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Landline number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Mobile</label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="Mobile number"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    <FaSave /> {modalMode === 'create' ? 'Create College Admin' : 'Update College Admin'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="view-info">
                <div className="info-section">
                  <h4>Account Information</h4>
                  <p><strong>Name:</strong> {selectedCollegeAdmin.profile.firstName} {selectedCollegeAdmin.profile.lastName}</p>
                  <p><strong>Email:</strong> {selectedCollegeAdmin.email}</p>
                  <p><strong>Department:</strong> {selectedCollegeAdmin.profile.department || 'Not specified'}</p>
                  <p><strong>Status:</strong> <span className={`status-text ${selectedCollegeAdmin.isActive ? 'active' : 'inactive'}`}>
                    {selectedCollegeAdmin.isActive ? 'Active' : 'Inactive'}
                  </span></p>
                  <p><strong>Created:</strong> {new Date(selectedCollegeAdmin.createdAt).toLocaleDateString()}</p>
                </div>

                {(selectedCollegeAdmin.profile.phone || selectedCollegeAdmin.profile.mobile) && (
                  <div className="info-section">
                    <h4>Contact Information</h4>
                    {selectedCollegeAdmin.profile.phone && (
                      <p><strong>Phone:</strong> {selectedCollegeAdmin.profile.phone}</p>
                    )}
                    {selectedCollegeAdmin.profile.mobile && (
                      <p><strong>Mobile:</strong> {selectedCollegeAdmin.profile.mobile}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCollegeAdmin;
